/* Bombay Mural motion helpers.
   Every feature bails out unless its body-class toggle, device
   capabilities, and the user's motion preference all allow it. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* --- Lift-reveal footer: measure the sticky offset ---
     A footer taller than the viewport can't pin at bottom: 0 (its top —
     the CTA — would never be visible), so the offset goes negative by
     the overflow: the footer's top pins to the viewport top during the
     lift, and the remainder scrolls in normally afterwards. */
  var footerObserver = null;

  function initFooterGuard() {
    if (!document.body.classList.contains('bm-footer-reveal')) return;
    var footer = document.querySelector('.shopify-section-group-footer-group');
    if (!footer) return;

    var apply = function () {
      var lift = Math.min(0, window.innerHeight - footer.offsetHeight);
      footer.style.setProperty('--bm-lift-bottom', lift + 'px');
    };

    if (footerObserver) footerObserver.disconnect();
    if ('ResizeObserver' in window) {
      footerObserver = new ResizeObserver(apply);
      footerObserver.observe(footer);
    }
    window.addEventListener('resize', apply, { passive: true });
    apply();
  }

  /* --- Cursor pill over artwork cards --- */
  function initCursorPill() {
    if (!document.body.classList.contains('bm-cursor')) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (!finePointer.matches || reducedMotion.matches) return;
    if (document.querySelector('.bm-cursor-pill')) return;

    var pill = document.createElement('div');
    pill.className = 'bm-cursor-pill';
    pill.setAttribute('aria-hidden', 'true');
    var inner = document.createElement('span');
    inner.className = 'bm-cursor-pill__inner';
    inner.textContent = document.body.getAttribute('data-bm-cursor-label') || 'View';
    pill.appendChild(inner);
    document.body.appendChild(pill);

    var targetX = -100;
    var targetY = -100;
    var x = -100;
    var y = -100;
    var active = false;
    var rafId = null;

    function loop() {
      x += (targetX - x) * 0.18;
      y += (targetY - y) * 0.18;
      pill.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      if (active || Math.abs(targetX - x) > 0.5 || Math.abs(targetY - y) > 0.5) {
        rafId = requestAnimationFrame(loop);
      } else {
        rafId = null;
      }
    }

    function start() {
      if (rafId === null) rafId = requestAnimationFrame(loop);
    }

    function inCard(node) {
      return node && node.closest && node.closest('.card-wrapper, .bm-portfolio__item');
    }

    document.addEventListener(
      'pointermove',
      function (event) {
        targetX = event.clientX;
        targetY = event.clientY;
        if (active) start();
      },
      { passive: true }
    );

    document.addEventListener('pointerover', function (event) {
      if (!inCard(event.target)) return;
      active = true;
      pill.classList.add('is-active');
      start();
    });

    document.addEventListener('pointerout', function (event) {
      if (!inCard(event.target)) return;
      if (inCard(event.relatedTarget)) return;
      active = false;
      pill.classList.remove('is-active');
    });

    reducedMotion.addEventListener('change', function (event) {
      if (event.matches) {
        active = false;
        pill.classList.remove('is-active');
      }
    });
  }

  /* --- Mumbai studio clock: a living sentence in the footer --- */
  if (!customElements.get('bm-studio-clock')) {
    customElements.define(
      'bm-studio-clock',
      class BmStudioClock extends HTMLElement {
        connectedCallback() {
          if (!('Intl' in window)) return;
          this.formatter = new Intl.DateTimeFormat('en-IN', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
          });
          this.hourFormatter = new Intl.DateTimeFormat('en-IN', {
            hour: 'numeric',
            hour12: false,
            timeZone: 'Asia/Kolkata',
          });
          this.tick = this.tick.bind(this);
          this.tick();
          this.timer = setInterval(this.tick, 60000);
        }

        tick() {
          var now = new Date();
          var hour = parseInt(this.hourFormatter.format(now), 10);
          var awake = hour >= 8 && hour < 20;
          var status = awake ? "there's paint on our hands." : 'the studio is asleep.';
          this.innerHTML =
            "It's <span class=\"bm-gold\">" +
            this.formatter.format(now) +
            '</span> in Mumbai — ' +
            status;
        }

        disconnectedCallback() {
          if (this.timer) clearInterval(this.timer);
        }
      }
    );
  }

  /* --- Footer reveal choreography ---
     Plain geometry instead of IntersectionObserver: rootMargin is silently
     ignored inside cross-origin iframes (Shopify admin preview), which left
     the logo permanently clipped there. The reveal is ON whenever the
     end-of-content sentinel has risen into (or above) the viewport. */
  var footerRevealBound = false;

  function initFooterReveal() {
    var footer = document.querySelector('.shopify-section-group-footer-group');
    if (!footer || !footer.querySelector('.bm-footer-logo, .bm-footer-wordmark')) return;
    var sentinel = document.querySelector('.bm-reveal-sentinel');
    if (!sentinel || (window.Shopify && window.Shopify.designMode)) {
      footer.classList.add('bm-footer-inview');
      return;
    }

    var ticking = false;
    var update = function () {
      ticking = false;
      var current = document.querySelector('.shopify-section-group-footer-group');
      if (!current) return;
      /* The sticky lift keeps the footer parked behind #MainContent, so the
         logo's geometry alone lies about visibility. Paint only when the
         logo sits in the viewport band AND the content's bottom edge (the
         sentinel) has peeled past it — covers normal flow, full lift, and
         pinned partial lift alike. */
      var logo = current.querySelector('.bm-footer-logo');
      var logoTop = (logo || sentinel).getBoundingClientRect().top;
      var revealed = sentinel.getBoundingClientRect().top < logoTop + 40;
      current.classList.toggle(
        'bm-footer-inview',
        revealed && logoTop < window.innerHeight * 0.92
      );
    };
    var request = function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    if (!footerRevealBound) {
      footerRevealBound = true;
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
    }
    document.documentElement.classList.add('bm-reveal-armed');
    update();
  }

  /* --- Gallery-light warmth fallback for browsers without scroll timelines --- */
  function initWarmthFallback() {
    if (!document.body.classList.contains('bm-gallery-light')) return;
    if (window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()')) return;
    if (reducedMotion.matches) return;
    var ticking = false;
    var update = function () {
      ticking = false;
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      /* Mirrors the bm-warmth keyframes: warm through the content, then
         cool back to zero so the footer reveals as true ink. */
      var peak = 0.85;
      var warmth = progress <= peak ? (progress / peak) * 0.09 : 0.09 * (1 - (progress - peak) / (1 - peak));
      document.body.style.setProperty('--bm-warmth', warmth.toFixed(4));
    };
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* --- Fit-to-column display lines ---
     Sizes a nowrap line so it spans its parent's content box exactly. Every
     length in the lockup is em-based (letter-spacing included), so width is
     linear in font-size and ONE measurement at a probe size is enough.
     `avail` is read BEFORE the probe is applied — reading it after would
     measure a box the probe itself had stretched. The 0.995 safety factor
     keeps the fitted line a hair inside the box so rounding can never
     bounce it wider on the next resize. */
  var fitters = [];

  /* `widest` (optional): measure these strings instead of the element's
     current text and size against the longest. The rotating word uses it so
     every word renders at ONE size — fitting each word to the column made
     STORIES and LEGENDS visibly different sizes. */
  function fitLine(el, widest) {
    var box = el.parentElement;
    if (!box) return;
    var avail = box.clientWidth;
    if (!avail) return;
    var min = parseFloat(el.dataset.fitMin) || 24;
    var max = parseFloat(el.dataset.fitMax) || 240;
    var probe = 100;
    el.style.fontSize = probe + 'px';
    var w = 0;
    if (widest && widest.measure) {
      w = widest.measure(probe);
    } else {
      w = el.getBoundingClientRect().width;
    }
    if (!w) return;
    var target = avail * 0.995;
    var size = Math.max(min, Math.min(max, (target * probe) / w));
    el.style.fontSize = size.toFixed(2) + 'px';

    /* Advance widths are quantised, so width is not perfectly linear in
       font-size — one probe lands ~1% out and the line overhangs its
       column. Correct against the size actually rendered. */
    var actual = widest && widest.measure ? widest.measure(size) : el.getBoundingClientRect().width;
    if (actual > 0 && Math.abs(actual - target) > 0.5) {
      size = Math.max(min, Math.min(max, (size * target) / actual));
      el.style.fontSize = size.toFixed(2) + 'px';
    }
  }

  function registerFit(fn) {
    fitters.push(fn);
    fn();
  }

  function refitAll() {
    for (var i = 0; i < fitters.length; i++) fitters[i]();
  }

  function unregisterFit(fn) {
    var i = fitters.indexOf(fn);
    if (i > -1) fitters.splice(i, 1);
  }

  var refitTicking = false;
  window.addEventListener(
    'resize',
    function () {
      if (refitTicking) return;
      refitTicking = true;
      requestAnimationFrame(function () {
        refitTicking = false;
        refitAll();
      });
    },
    { passive: true }
  );

  /* The first fit measures whatever face is available; once the real face
     lands its metrics differ, so measure again. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(refitAll).catch(function () {});
  }

  if (!customElements.get('bm-fit-line')) {
    customElements.define(
      'bm-fit-line',
      class BmFitLine extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.fit = function () {
            fitLine(self);
          };
          registerFit(this.fit);
        }

        disconnectedCallback() {
          if (this.fit) unregisterFit(this.fit);
        }
      }
    );
  }

  /* --- Rotating hero word --- */
  if (!customElements.get('bm-word-rotate')) {
    customElements.define(
      'bm-word-rotate',
      class BmWordRotate extends HTMLElement {
        connectedCallback() {
          this.wordEl = this.querySelector('.bm-word-rotate__word');
          var words = (this.dataset.words || '')
            .split(',')
            .map(function (w) {
              return w.trim();
            })
            .filter(Boolean);
          if (!this.wordEl) return;
          var self = this;

          /* data-fit: the word is sized to FILL its column, so every word
             lands flush with the line above it. That replaces the old
             min-width reservation — which sized the GRID COLUMN and so
             forced the headline to wrap whenever this word grew. Fitting is
             layout, not motion: it runs in the editor and under reduced
             motion too, where only the cycling is suppressed. */
          if (this.hasAttribute('data-fit')) {
            /* Measure every word at the probe size and size against the
               longest, so all of them share ONE font-size. Shorter words
               simply stop short of the column edge — which is the point:
               the client wants them the same size, not the same width. */
            var widest = {
              measure: function (size) {
                var before = self.wordEl.textContent;
                self.style.fontSize = size + 'px';
                var max = 0;
                for (var i = 0; i < words.length; i++) {
                  self.wordEl.textContent = words[i];
                  max = Math.max(max, self.getBoundingClientRect().width);
                }
                self.wordEl.textContent = before;
                return max;
              },
            };
            this.fit = function () {
              fitLine(self, widest);
            };
            registerFit(this.fit);
          } else if (words.length > 1) {
            /* Reserve the widest word's space so cycling never shifts layout. */
            var probe = document.createElement('span');
            probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;';
            this.appendChild(probe);
            var widest = 0;
            words.forEach(function (w) {
              probe.textContent = w;
              widest = Math.max(widest, probe.offsetWidth);
            });
            probe.remove();
            if (widest > 0) this.style.minWidth = widest + 'px';
          }

          if (words.length < 2) return;
          if (window.Shopify && window.Shopify.designMode) return;
          if (reducedMotion.matches) return;

          this.words = words;
          this.index = 0;

          /* Seconds per word is a section setting; the old value stays the
             fallback so an unset section behaves exactly as it did. */
          var hold = parseFloat(this.dataset.interval);
          hold = hold > 0 ? hold * 1000 : 2800;

          this.timer = setInterval(function () {
            self.wordEl.classList.add('is-out');
            setTimeout(function () {
              self.index = (self.index + 1) % self.words.length;
              self.wordEl.textContent = self.words[self.index];
              self.wordEl.classList.remove('is-out');
            }, 200);
          }, hold);
        }

        disconnectedCallback() {
          if (this.timer) clearInterval(this.timer);
          if (this.fit) unregisterFit(this.fit);
        }
      }
    );
  }

  /* --- Animated stats counter --- */
  if (!customElements.get('bm-count-up')) {
    customElements.define(
      'bm-count-up',
      class BmCountUp extends HTMLElement {
        connectedCallback() {
          this.valueEl = this.querySelector('.bm-stats__value');
          var target = parseFloat(this.dataset.target);
          if (!this.valueEl || !isFinite(target)) return;
          this.target = target;
          /* en-IN, not the document lang: the client's numbers are quoted
             the Indian way (1,00,000), and the Liquid fallback groups them
             the same. A plain 'en' would render 100,000 and the number
             would change the moment JS took over. */
          this.formatter =
            'Intl' in window && this.hasAttribute('data-grouping')
              ? new Intl.NumberFormat('en-IN')
              : {
                  format: function (n) {
                    return String(n);
                  },
                };

          /* Always land on the final formatted value first; the count-up
             only replaces it when it is safe to animate. */
          this.valueEl.textContent = this.formatter.format(this.target);

          if (window.Shopify && window.Shopify.designMode) return;
          if (reducedMotion.matches) return;
          if (!('IntersectionObserver' in window)) return;

          this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.35 });
          this.observer.observe(this);
        }

        onIntersect(entries) {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.observer = null;

          var duration = parseInt(this.dataset.duration, 10) || 1600;
          var startTime = null;
          var self = this;

          var step = function (timestamp) {
            if (startTime === null) startTime = timestamp;
            var t = Math.min((timestamp - startTime) / duration, 1);
            var eased = 1 - Math.pow(1 - t, 3);
            self.valueEl.textContent = self.formatter.format(Math.round(eased * self.target));
            if (t < 1 && !reducedMotion.matches) {
              self.rafId = requestAnimationFrame(step);
            } else {
              self.valueEl.textContent = self.formatter.format(self.target);
              self.rafId = null;
            }
          };

          this.valueEl.textContent = this.formatter.format(0);
          this.rafId = requestAnimationFrame(step);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.rafId) cancelAnimationFrame(this.rafId);
        }
      }
    );
  }

  function init() {
    initFooterGuard();
    initCursorPill();
    initFooterReveal();
    initWarmthFallback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor reloads sections dynamically; re-check footer features. */
  document.addEventListener('shopify:section:load', function () {
    initFooterGuard();
    initFooterReveal();
  });
})();
